import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatChatHistoryUpdatedAt } from './format-chat-history-updated-at';
import type { AdvisorHistoryThreadRow } from './advisor-thread-history-list';
import { createAdvisorV2ThreadApi } from './create-advisor-v2-thread-api';

export type UseAdvisorV2ThreadHistoryOptions = {
  enabled: boolean;
  /** Refetch the list when this value changes (e.g. partner org id). */
  listQueryKey?: string;
  useCase: string;
  /**
 * When true, ``listThreads`` uses ``GET /v2/chat/threads`` without ``use_case`` (all org Kin lanes).
 * ``useCase`` is still the default lane for ``deleteThread`` / ``fetchMessages`` when no per-thread override is passed.
 */
  listAllCoreThreads?: boolean;
  baseUrl: string;
  fetch: typeof globalThis.fetch;
  buildHeaders: () => Promise<Record<string, string>>;
  formatError: (raw: string) => string;
};

export type UseAdvisorV2ThreadHistoryResult = {
  threads: AdvisorHistoryThreadRow[];
  loading: boolean;
  errorText: string | null;
  deletingThreadId: string | null;
  clearError: () => void;
  /** Map a raw error (e.g. ``Error.message``) through ``formatError`` and show it in the drawer. */
  reportError: (raw: string) => void;
  deleteThread: (threadId: string, threadUseCase?: string) => Promise<void>;
};

/**
 * Loads persisted V2 thread titles for the history drawer; supports any ``use_case`` when
 * paired with app-specific ``fetch`` (401 replay) and ``buildHeaders`` (auth / org).
 * When a fetch starts, the in-memory list is cleared first so a stale list is not shown
 * after ``listQueryKey`` / org changes or when reopening the drawer.
 *
 * ``fetch`` and ``buildHeaders`` are read via refs so unstable function identities do not
 * recreate the API client or re-trigger the load effect (admin Kin historically passed
 * ``globalThis.fetch.bind(globalThis)`` inline, which caused an infinite Loading loop).
 */
export function useAdvisorV2ThreadHistory(options: UseAdvisorV2ThreadHistoryOptions): UseAdvisorV2ThreadHistoryResult {
  const { enabled, listQueryKey, useCase, listAllCoreThreads = false, baseUrl } = options;

  const buildHeadersRef = useRef(options.buildHeaders);
  buildHeadersRef.current = options.buildHeaders;

  const formatErrorRef = useRef(options.formatError);
  formatErrorRef.current = options.formatError;

  // Keep fetch on a ref so callers that pass an unstable function identity
  // (e.g. ``globalThis.fetch.bind(globalThis)`` inline) do not recreate the
  // client and re-trigger the load effect forever.
  const fetchRef = useRef(options.fetch);
  fetchRef.current = options.fetch;

  const client = useMemo(
    () =>
      createAdvisorV2ThreadApi({
        baseUrl,
        useCase,
        listAllCoreThreads,
        fetch: ((input, init) => fetchRef.current(input, init)) as typeof globalThis.fetch,
        buildHeaders: () => buildHeadersRef.current(),
      }),
    [baseUrl, useCase, listAllCoreThreads]
  );

  const [threads, setThreads] = useState<AdvisorHistoryThreadRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  const clearError = useCallback(() => setErrorText(null), []);

  const reportError = useCallback((raw: string) => {
    setErrorText(formatErrorRef.current(raw));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setErrorText(null);
    setThreads([]);
    void client
      .listThreads()
      .then(rows => {
        if (cancelled) return;
        setThreads(
          rows.map(r => ({
            id: r.id,
            title: r.title,
            useCase: r.use_case,
            updatedAtLabel: formatChatHistoryUpdatedAt(r.updated_at ?? undefined),
          }))
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setErrorText(formatErrorRef.current(err instanceof Error ? err.message : String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, listQueryKey, client]);

  const deleteThread = useCallback(
    async (threadId: string, threadUseCase?: string) => {
      setDeletingThreadId(threadId);
      try {
        await client.deleteThread(threadId, threadUseCase ? { useCase: threadUseCase } : undefined);
        setThreads(prev => prev.filter(t => t.id !== threadId));
      } catch (err: unknown) {
        const raw = err instanceof Error ? err.message : String(err);
        setErrorText(formatErrorRef.current(raw));
        throw err;
      } finally {
        setDeletingThreadId(null);
      }
    },
    [client]
  );

  return {
    threads,
    loading,
    errorText,
    deletingThreadId,
    clearError,
    reportError,
    deleteThread,
  };
}
